import { useState, useEffect, type CSSProperties } from 'react'
import {
  Settings, Bot, Brain, Webhook, Eye, EyeOff, CheckCircle, XCircle,
  Loader, Save, Trash2, KeyRound, ShieldCheck, Globe, Zap, Scale,
} from 'lucide-react'
import { useLang } from '../i18n/LangContext'
import { getApiKey, setApiKey, testConnection, type AgentMode } from '../engine/AgentBridge'
import { getUseRealApi, setUseRealApi } from '../engine/ToolExecutor'
import { getUseLLMJudge, setUseLLMJudge, getJudgeModel, setJudgeModel } from '../engine/LLMJudge'

interface TestStatus {
  success: boolean
  message: string
}

const OPENAI_MODELS = ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'o1', 'o1-mini']
const ANTHROPIC_MODELS = [
  'claude-3-5-sonnet-20241022',
  'claude-3-5-haiku-20241022',
  'claude-3-opus-20240229',
]
const WEBHOOK_STORAGE_KEY = 'decisioncraft_webhook_url'
const API_KEYS_STORAGE_KEY = 'decisioncraft_api_keys'

const inputStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text-primary)',
  borderRadius: '0.5rem',
  padding: '0.625rem 0.75rem',
}

const cardStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-card)',
  border: '1px solid var(--color-border)',
}

export default function SettingsPage() {
  const { lang } = useLang()
  const [openaiKey, setOpenaiKey] = useState('')
  const [anthropicKey, setAnthropicKey] = useState('')
  const [webhookUrl, setWebhookUrl] = useState('')
  const [useRealApi, setRealApiEnabled] = useState(false)
  const [useLLMJudgeEnabled, setLLMJudgeEnabled] = useState(false)
  const [judgeModel, setJudgeModelValue] = useState('gpt-4o-mini')
  const [openaiModel, setOpenaiModel] = useState(OPENAI_MODELS[0])
  const [anthropicModel, setAnthropicModel] = useState(ANTHROPIC_MODELS[0])
  const [showOpenAIKey, setShowOpenAIKey] = useState(false)
  const [showAnthropicKey, setShowAnthropicKey] = useState(false)
  const [openaiStatus, setOpenaiStatus] = useState<TestStatus | null>(null)
  const [anthropicStatus, setAnthropicStatus] = useState<TestStatus | null>(null)
  const [testing, setTesting] = useState<AgentMode | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  // Load existing keys on mount
  useEffect(() => {
    setOpenaiKey(getApiKey('openai'))
    setAnthropicKey(getApiKey('anthropic'))
    const savedWebhook = typeof localStorage !== 'undefined'
      ? localStorage.getItem(WEBHOOK_STORAGE_KEY) ?? ''
      : ''
    setWebhookUrl(savedWebhook)
    setRealApiEnabled(getUseRealApi())
    setLLMJudgeEnabled(getUseLLMJudge())
    setJudgeModelValue(getJudgeModel())
  }, [])

  // Auto-hide toast
  useEffect(() => {
    if (!toast) return
    const timer = setTimeout(() => setToast(null), 2500)
    return () => clearTimeout(timer)
  }, [toast])

  const handleTestOpenAI = async () => {
    setTesting('openai')
    setOpenaiStatus(null)
    try {
      const result = await testConnection('openai', openaiKey, openaiModel)
      setOpenaiStatus({ success: result.success, message: result.message })
    } catch (err) {
      setOpenaiStatus({
        success: false,
        message: err instanceof Error ? err.message : (lang === 'zh' ? '未知错误' : 'Unknown error'),
      })
    } finally {
      setTesting(null)
    }
  }

  const handleTestAnthropic = async () => {
    setTesting('anthropic')
    setAnthropicStatus(null)
    try {
      const result = await testConnection('anthropic', anthropicKey, anthropicModel)
      setAnthropicStatus({ success: result.success, message: result.message })
    } catch (err) {
      setAnthropicStatus({
        success: false,
        message: err instanceof Error ? err.message : (lang === 'zh' ? '未知错误' : 'Unknown error'),
      })
    } finally {
      setTesting(null)
    }
  }

  const handleSaveOpenAI = () => {
    setApiKey('openai', openaiKey)
    setToast(lang === 'zh' ? '已保存！' : 'Saved!')
  }

  const handleSaveAnthropic = () => {
    setApiKey('anthropic', anthropicKey)
    setToast(lang === 'zh' ? '已保存！' : 'Saved!')
  }

  const handleSaveWebhook = () => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(WEBHOOK_STORAGE_KEY, webhookUrl)
    }
    setToast(lang === 'zh' ? '已保存！' : 'Saved!')
  }

  const handleClearAll = () => {
    setApiKey('openai', '')
    setApiKey('anthropic', '')
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(WEBHOOK_STORAGE_KEY)
      localStorage.removeItem(API_KEYS_STORAGE_KEY)
    }
    setOpenaiKey('')
    setAnthropicKey('')
    setWebhookUrl('')
    setOpenaiStatus(null)
    setAnthropicStatus(null)
    setToast(lang === 'zh' ? '已清除所有密钥' : 'All keys cleared')
  }

  const renderStatus = (status: TestStatus | null) => {
    if (!status) return null
    return (
      <div
        className="flex items-center gap-2 mt-3 px-3 py-2 rounded-lg text-sm"
        style={{
          backgroundColor: status.success ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)',
          color: status.success ? 'var(--color-success)' : 'var(--color-danger)',
          border: `1px solid ${status.success ? 'rgba(74,222,128,0.25)' : 'rgba(248,113,113,0.25)'}`,
        }}
      >
        {status.success ? <CheckCircle size={15} /> : <XCircle size={15} />}
        <span>{status.message}</span>
      </div>
    )
  }

  return (
    <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <Settings size={24} style={{ color: 'var(--color-accent)' }} />
            {lang === 'zh' ? '设置' : 'Settings'}
          </h1>
        </div>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          {lang === 'zh' ? '管理API密钥和Agent配置' : 'Manage API keys and agent configurations'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* OpenAI Configuration Card */}
        <div className="rounded-xl p-6" style={cardStyle}>
          <h2 className="text-lg font-semibold mb-5 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <Bot size={18} style={{ color: 'var(--color-accent)' }} />
            {lang === 'zh' ? 'OpenAI API' : 'OpenAI API'}
          </h2>

          <div className="space-y-4">
            {/* API Key Input */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                {lang === 'zh' ? 'API 密钥' : 'API Key'}
              </label>
              <div className="relative">
                <input
                  type={showOpenAIKey ? 'text' : 'password'}
                  value={openaiKey}
                  onChange={e => setOpenaiKey(e.target.value)}
                  placeholder="sk-..."
                  className="w-full text-sm outline-none pr-10"
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowOpenAIKey(!showOpenAIKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors"
                  style={{ color: 'var(--color-text-muted)' }}
                  title={showOpenAIKey ? (lang === 'zh' ? '隐藏' : 'Hide') : (lang === 'zh' ? '显示' : 'Show')}
                >
                  {showOpenAIKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p className="mt-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {lang === 'zh'
                  ? '提示：生产环境可在 Cloudflare 设置 OPENAI_API_KEY 环境变量（推荐，更安全）；本地填写密钥也可。'
                  : 'Tip: In production, set OPENAI_API_KEY as Cloudflare env var (recommended, more secure). Local key also works.'}
              </p>
            </div>

            {/* Model Dropdown */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                {lang === 'zh' ? '模型' : 'Model'}
              </label>
              <select
                value={openaiModel}
                onChange={e => setOpenaiModel(e.target.value)}
                className="w-full text-sm outline-none appearance-none"
                style={inputStyle}
              >
                {OPENAI_MODELS.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            {/* Test Connection + Status */}
            <button
              onClick={handleTestOpenAI}
              disabled={testing !== null}
              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-border)',
                opacity: testing !== null ? 0.6 : 1,
                cursor: testing !== null ? 'not-allowed' : 'pointer',
              }}
            >
              {testing === 'openai' ? (
                <Loader size={15} className="animate-spin" />
              ) : (
                <KeyRound size={15} />
              )}
              {lang === 'zh' ? '测试连接' : 'Test Connection'}
            </button>

            {renderStatus(openaiStatus)}

            {/* Save Button */}
            <button
              onClick={handleSaveOpenAI}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors"
              style={{
                backgroundColor: 'var(--color-accent)',
                color: '#fff',
              }}
            >
              <Save size={15} />
              {lang === 'zh' ? '保存' : 'Save'}
            </button>
          </div>
        </div>

        {/* Anthropic Configuration Card */}
        <div className="rounded-xl p-6" style={cardStyle}>
          <h2 className="text-lg font-semibold mb-5 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <Brain size={18} style={{ color: 'var(--color-accent)' }} />
            {lang === 'zh' ? 'Anthropic API (Claude)' : 'Anthropic API (Claude)'}
          </h2>

          <div className="space-y-4">
            {/* API Key Input */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                {lang === 'zh' ? 'API 密钥' : 'API Key'}
              </label>
              <div className="relative">
                <input
                  type={showAnthropicKey ? 'text' : 'password'}
                  value={anthropicKey}
                  onChange={e => setAnthropicKey(e.target.value)}
                  placeholder="sk-ant-..."
                  className="w-full text-sm outline-none pr-10"
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowAnthropicKey(!showAnthropicKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors"
                  style={{ color: 'var(--color-text-muted)' }}
                  title={showAnthropicKey ? (lang === 'zh' ? '隐藏' : 'Hide') : (lang === 'zh' ? '显示' : 'Show')}
                >
                  {showAnthropicKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p className="mt-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {lang === 'zh'
                  ? '提示：生产环境可在 Cloudflare 设置 ANTHROPIC_API_KEY 环境变量（推荐，更安全）。'
                  : 'Tip: In production, set ANTHROPIC_API_KEY as Cloudflare env var (recommended, more secure).'}
              </p>
            </div>

            {/* Model Dropdown */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                {lang === 'zh' ? '模型' : 'Model'}
              </label>
              <select
                value={anthropicModel}
                onChange={e => setAnthropicModel(e.target.value)}
                className="w-full text-sm outline-none appearance-none"
                style={inputStyle}
              >
                {ANTHROPIC_MODELS.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            {/* Test Connection + Status */}
            <button
              onClick={handleTestAnthropic}
              disabled={testing !== null}
              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-border)',
                opacity: testing !== null ? 0.6 : 1,
                cursor: testing !== null ? 'not-allowed' : 'pointer',
              }}
            >
              {testing === 'anthropic' ? (
                <Loader size={15} className="animate-spin" />
              ) : (
                <KeyRound size={15} />
              )}
              {lang === 'zh' ? '测试连接' : 'Test Connection'}
            </button>

            {renderStatus(anthropicStatus)}

            {/* Save Button */}
            <button
              onClick={handleSaveAnthropic}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors"
              style={{
                backgroundColor: 'var(--color-accent)',
                color: '#fff',
              }}
            >
              <Save size={15} />
              {lang === 'zh' ? '保存' : 'Save'}
            </button>
          </div>
        </div>

        {/* Webhook Agent Configuration Card */}
        <div className="rounded-xl p-6" style={cardStyle}>
          <h2 className="text-lg font-semibold mb-5 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <Webhook size={18} style={{ color: 'var(--color-accent)' }} />
            {lang === 'zh' ? 'Webhook Agent (Codex/Claude Code/OpenClaw)' : 'Webhook Agent (Codex/Claude Code/OpenClaw)'}
          </h2>

          <div className="space-y-4">
            {/* URL Input */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                {lang === 'zh' ? 'Webhook URL' : 'Webhook URL'}
              </label>
              <input
                type="text"
                value={webhookUrl}
                onChange={e => setWebhookUrl(e.target.value)}
                placeholder="https://your-agent.com/execute"
                className="w-full text-sm outline-none"
                style={inputStyle}
              />
            </div>

            {/* Description */}
            <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
              {lang === 'zh'
                ? '外部Agent通过webhook接收任务，通过 /api/tools/execute 调用工具，完成后返回轨迹'
                : 'External agents receive tasks via webhook, call tools via /api/tools/execute, and return traces when complete'}
            </p>

            {/* Save Button */}
            <button
              onClick={handleSaveWebhook}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors"
              style={{
                backgroundColor: 'var(--color-accent)',
                color: '#fff',
              }}
            >
              <Save size={15} />
              {lang === 'zh' ? '保存' : 'Save'}
            </button>
          </div>
        </div>

        {/* Real API Tools Toggle Card */}
        <div className="rounded-xl p-6" style={cardStyle}>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <Globe size={18} style={{ color: 'var(--color-accent)' }} />
            {lang === 'zh' ? '真实API工具' : 'Real API Tools'}
          </h2>
          <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
            {lang === 'zh'
              ? '启用后，搜索和地图工具将调用真实API（DuckDuckGo + OpenStreetMap），而非模拟数据。失败时自动回退到模拟数据。'
              : 'When enabled, search and maps tools call real APIs (DuckDuckGo + OpenStreetMap) instead of simulated data. Falls back to simulated data on failure.'}
          </p>
          <button
            onClick={() => {
              const newVal = !useRealApi
              setUseRealApi(newVal)
              setRealApiEnabled(newVal)
            }}
            className="flex items-center gap-3 px-4 py-3 rounded-lg w-full"
            style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              border: `1px solid ${useRealApi ? 'var(--color-success)' : 'var(--color-border)'}`,
            }}
          >
            <div
              className="w-10 h-5 rounded-full relative transition-all"
              style={{ backgroundColor: useRealApi ? 'var(--color-success)' : 'var(--color-border)' }}
            >
              <div
                className="absolute top-0.5 w-4 h-4 rounded-full transition-all"
                style={{
                  left: useRealApi ? '22px' : '2px',
                  backgroundColor: 'var(--color-text-primary)',
                }}
              />
            </div>
            <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
              {useRealApi
                ? (lang === 'zh' ? '已启用真实API' : 'Real API enabled')
                : (lang === 'zh' ? '使用模拟数据' : 'Using simulated data')
              }
            </span>
            {useRealApi && <Zap size={14} style={{ color: 'var(--color-warning)' }} />}
          </button>
          <div className="mt-3 text-xs space-y-1" style={{ color: 'var(--color-text-muted)' }}>
            <div>• {lang === 'zh' ? '搜索工具：DuckDuckGo Instant Answer API + Wikipedia' : 'Search: DuckDuckGo Instant Answer API + Wikipedia'}</div>
            <div>• {lang === 'zh' ? '地图工具：OpenStreetMap Nominatim + Haversine距离' : 'Maps: OpenStreetMap Nominatim + Haversine distance'}</div>
            <div>• {lang === 'zh' ? '计算器/表格：本地执行，无需API' : 'Calculator/Spreadsheet: executed locally, no API needed'}</div>
          </div>
        </div>

        {/* LLM-as-a-Judge Toggle Card */}
        <div className="rounded-xl p-6" style={cardStyle}>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <Scale size={18} style={{ color: 'var(--color-accent)' }} />
            {lang === 'zh' ? 'LLM 评判员' : 'LLM-as-a-Judge'}
          </h2>
          <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
            {lang === 'zh'
              ? '启用后，每条评测结果会额外调用一个LLM（默认 gpt-4o-mini）评估答案的完整性、准确性、推理质量。这能弥补正则解析的局限性，让跨模型对比更公平。'
              : 'When enabled, each evaluation result is additionally scored by an LLM (default gpt-4o-mini) on completeness, accuracy, and reasoning quality. This compensates for regex parsing limitations and makes cross-model comparison fairer.'}
          </p>
          <button
            onClick={() => {
              const newVal = !useLLMJudgeEnabled
              setUseLLMJudge(newVal)
              setLLMJudgeEnabled(newVal)
            }}
            className="flex items-center gap-3 px-4 py-3 rounded-lg w-full"
            style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              border: `1px solid ${useLLMJudgeEnabled ? 'var(--color-success)' : 'var(--color-border)'}`,
            }}
          >
            <div
              className="w-10 h-5 rounded-full relative transition-all"
              style={{ backgroundColor: useLLMJudgeEnabled ? 'var(--color-success)' : 'var(--color-border)' }}
            >
              <div
                className="absolute top-0.5 w-4 h-4 rounded-full transition-all"
                style={{
                  left: useLLMJudgeEnabled ? '22px' : '2px',
                  backgroundColor: 'var(--color-text-primary)',
                }}
              />
            </div>
            <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
              {useLLMJudgeEnabled
                ? (lang === 'zh' ? '已启用LLM评判' : 'LLM Judge enabled')
                : (lang === 'zh' ? '未启用' : 'Disabled')
              }
            </span>
            {useLLMJudgeEnabled && <Scale size={14} style={{ color: 'var(--color-warning)' }} />}
          </button>
          {useLLMJudgeEnabled && (
            <div className="mt-3">
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                {lang === 'zh' ? '评判模型' : 'Judge Model'}
              </label>
              <select
                value={judgeModel}
                onChange={e => {
                  const v = e.target.value
                  setJudgeModel(v)
                  setJudgeModelValue(v)
                }}
                className="w-full text-sm outline-none appearance-none"
                style={inputStyle}
              >
                <option value="gpt-4o-mini">gpt-4o-mini ({lang === 'zh' ? '推荐，便宜快速' : 'recommended, cheap'})</option>
                <option value="gpt-4o">gpt-4o ({lang === 'zh' ? '更准确但更贵' : 'more accurate but pricier'})</option>
                <option value="gpt-4-turbo">gpt-4-turbo</option>
              </select>
              <p className="mt-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {lang === 'zh'
                  ? '提示：评判使用上方的 OpenAI API Key。每个任务额外消耗约 200 tokens。'
                  : 'Note: judging uses the OpenAI API key above. Each task costs ~200 additional tokens.'}
              </p>
            </div>
          )}
        </div>

        {/* API Usage Info Card */}
        <div className="rounded-xl p-6" style={cardStyle}>
          <h2 className="text-lg font-semibold mb-5 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <ShieldCheck size={18} style={{ color: 'var(--color-success)' }} />
            {lang === 'zh' ? 'API 使用说明' : 'API Usage Info'}
          </h2>

          <div className="space-y-3">
            <div
              className="flex items-start gap-2.5 p-3 rounded-lg"
              style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
            >
              <KeyRound size={15} className="mt-0.5 shrink-0" style={{ color: 'var(--color-accent)' }} />
              <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                {lang === 'zh'
                  ? 'API 密钥仅存储在您本地浏览器中（localStorage），不会上传到服务器。'
                  : 'API keys are stored locally in your browser only (localStorage).'}
              </p>
            </div>

            <div
              className="flex items-start gap-2.5 p-3 rounded-lg"
              style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
            >
              <ShieldCheck size={15} className="mt-0.5 shrink-0" style={{ color: 'var(--color-success)' }} />
              <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                {lang === 'zh'
                  ? '密钥永远不会发送到 DecisionCraft 服务器。'
                  : 'Keys are never sent to DecisionCraft servers.'}
              </p>
            </div>

            <div
              className="flex items-start gap-2.5 p-3 rounded-lg"
              style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
            >
              <Bot size={15} className="mt-0.5 shrink-0" style={{ color: 'var(--color-accent)' }} />
              <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                {lang === 'zh'
                  ? '所有 API 调用均直接从您的浏览器发送至对应服务提供商。'
                  : 'All API calls go directly from your browser to the provider.'}
              </p>
            </div>

            {/* Clear All Keys Button */}
            <button
              onClick={handleClearAll}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors mt-2"
              style={{
                backgroundColor: 'rgba(248,113,113,0.12)',
                color: 'var(--color-danger)',
                border: '1px solid rgba(248,113,113,0.25)',
              }}
            >
              <Trash2 size={15} />
              {lang === 'zh' ? '清除所有密钥' : 'Clear All Keys'}
            </button>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2"
          style={{
            backgroundColor: 'var(--color-bg-card)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-accent)',
          }}
        >
          <CheckCircle size={15} style={{ color: 'var(--color-success)' }} />
          {toast}
        </div>
      )}
    </div>
  )
}
